/**
 * Camera slug utilities for programmatic SEO pages.
 * Generates URL-friendly slugs from manufacturer + model name.
 */

import { CAMERA_MANUFACTURERS_EXTENDED, GANZ_SECURITY_CAMERAS } from '@/data/cameras';
import { PAGED_EXTRA_CAMERAS } from '@/data/cameras-paged-extras.generated';
import { INDEXED_LEGACY_CAMERAS } from '@/data/cameras-indexed-legacy.generated';
import { isNonCamera } from '@/lib/catalogItemKind';
import { RETIRED_CAMERA_SLUG_SET } from '@/data/retiredCameraSlugs.generated';
import { SPEC_CORRECTIONS } from '@/data/specCorrections.generated';
import { cameraPath, generateBrandSlug, generateModelSlug } from '@/lib/catalogRoutes';
import type { CameraModel, ConnectionType } from '@/components/cctv-designer-v2/types';

export interface CameraPageData {
  manufacturer: string;
  model: string;
  /** Legacy flat slug, still the lookup key and the source of the 301 map. */
  slug: string;
  /** Root segment, e.g. "dahua". */
  brandSlug: string;
  /** Second segment, e.g. "ipc-hfw1230s-s5". */
  modelSlug: string;
  /** Canonical address, e.g. "/dahua/ipc-hfw1230s-s5". */
  path: string;
  hfov: number;
  range: number;
  irRange: number;
  type: 'bullet' | 'dome' | 'ptz' | 'panoramic' | 'turret';
  resolution?: string;
  focalLength?: string;
  superLowLight?: boolean;
  connectionType: ConnectionType;
  /** Figures the maker has not confirmed to us: an estimated angle, or a row kept only for its indexed page. */
  figureSource?: 'estimated' | 'unconfirmed';
  /** The maker's own product page or datasheet the figures were read from. */
  datasheetUrl?: string;
}

/**
 * Infer the transmission standard from manufacturer + model code prefix.
 *
 * We only emit per-camera SEO pages for `ip` (the catalog is 96% IP and
 * analog SKUs have niche search demand). HD-TVI / HDCVI / SD-analog
 * SKUs are kept in the runtime catalog (so existing /camera/<slug> URLs
 * the user might have linked to still work via getCameraBySlug), but
 * are skipped by `getAllCamerasForStaticPages()` and the sitemap.
 *
 * Defaults to 'ip' for any pattern we don't recognise — assumes IP
 * unless the prefix clearly identifies analog. Conservative: under-
 * filtering > breaking a real IP camera page.
 */
/**
 * Coax-only SKUs with no prefix that identifies them as analog. Taken from the
 * `connectivity` field of the ch-bas CC0 dataset (2026-07-28 import) — the
 * model strings themselves give nothing to pattern-match on, and without this
 * they would be prerendered as IP camera pages.
 */
const COAX_ONLY_MODELS = new Set([
  'ACTi/Y31', 'ACTi/Y32', 'ACTi/Y35', 'ACTi/Y55', 'ACTi/Y71', 'ACTi/Y72',
  'Bosch/MIC SERIES 550 IR (MIC-550IR55-P)',
  'Speco Technologies/VLD10',
]);

export function inferConnectionType(brand: string, modelName: string): ConnectionType {
  const m = String(modelName || '').toUpperCase();
  const b = String(brand || '');
  if (COAX_ONLY_MODELS.has(`${b}/${m}`)) return 'analog';
  // Hikvision Turbo HD (HD-TVI) — analog. DS-2CC is the older analog line.
  if (b === 'Hikvision' || b === 'HiLook' || b === 'HiWatch') {
    if (/^DS-2C[EC]/.test(m)) return 'turbo-hd';
    return 'ip';
  }
  // ABUS Analog HD (HD-TVI/AHD/HD-CVI switchable) and legacy analog
  if (b === 'ABUS') {
    if (/^HDCC/.test(m)) return 'turbo-hd';
    if (/^TVCC/.test(m)) return 'analog';
    return 'ip';
  }
  // Dahua HDCVI — analog
  if (b === 'Dahua' || b === 'Amcrest' || b === 'Lorex') {
    if (/^(HAC|DH-HAC|XVR|DH-XVR|DHI-XVR)-/.test(m)) return 'hdcvi';
    return 'ip';
  }
  // Hanwha / Wisenet / Samsung — legacy analog SCO/SCB/SCV/SDC + HCV (HD)
  if (b === 'Hanwha' || b === 'Wisenet' || b === 'Samsung') {
    if (/^(SCO|SCB|SCV|SCC|SCD|SDC|SDR|SHR|SVR|SDN|SHB|SOC)-/.test(m)) return 'analog';
    if (/^HC[VDBCOPFHTM]-/.test(m)) return 'hdcvi';
    return 'ip';
  }
  // All other brands default to IP (most are IP-only manufacturers anyway)
  return 'ip';
}

/** Convert manufacturer + model to a URL-friendly slug */
export function generateCameraSlug(manufacturer: string, model: string): string {
  return `${manufacturer}-${model}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Build flat array of all cameras with slugs (cached) */
let _cachedCameras: CameraPageData[] | null = null;

const INDEXED_LEGACY_ROWS = new Set<object>(INDEXED_LEGACY_CAMERAS);
const FIGURES = ['hfov', 'hfovTele', 'resolution', 'type', 'irRange', 'focalLength', 'superLowLight'] as const;
function pickFigures(fix: object): Partial<CameraModel> {
  const out: Record<string, unknown> = {};
  for (const k of FIGURES) if (k in fix) out[k] = (fix as Record<string, unknown>)[k];
  return out as Partial<CameraModel>;
}

export function getAllCamerasWithSlugs(): CameraPageData[] {
  if (_cachedCameras) return _cachedCameras;

  const seen = new Set<string>();
  const cameras: CameraPageData[] = [];

  // PAGED_EXTRA_CAMERAS: cameras with the maker's own figures that sit only in the catalog shards (see
  // scripts/generate-paged-extras.mts), last, so a brand list's row of the same name keeps its page.
  // INDEXED_LEGACY_CAMERAS: pages Google indexes that the catalogue no longer gives one, kept with the figures they
  // showed, last, so any page built from the maker's own figures wins its address.
  for (const mfg of [...CAMERA_MANUFACTURERS_EXTENDED, GANZ_SECURITY_CAMERAS, ...PAGED_EXTRA_CAMERAS, ...INDEXED_LEGACY_CAMERAS]) {
    const legacy = INDEXED_LEGACY_ROWS.has(mfg);
    for (const listed of mfg.models) {
      // the datasheet corrections the planner and its shards apply (cameraManufacturers.ts), so a page states the
      // same figures as the planner; the figures only: names, addresses and the page set stay. Kept legacy pages
      // keep the figures they showed.
      const fix = legacy ? undefined : SPEC_CORRECTIONS[`${mfg.name}|${listed.name}`];
      const model = fix ? { ...listed, ...pickFigures(fix) } : listed;
      const slug = generateCameraSlug(mfg.name, model.name);
      // Skip duplicates
      if (seen.has(slug)) continue;
      seen.add(slug);

      const brandSlug = generateBrandSlug(mfg.name);
      const modelSlug = generateModelSlug(model.name);

      cameras.push({
        manufacturer: mfg.name,
        model: model.name,
        slug,
        brandSlug,
        modelSlug,
        path: cameraPath(brandSlug, modelSlug, slug),
        hfov: model.hfov,
        range: model.range,
        irRange: model.irRange,
        type: model.type,
        resolution: model.resolution,
        focalLength: model.focalLength,
        superLowLight: model.superLowLight,
        figureSource: (model as { figureSource?: 'estimated' | 'unconfirmed' }).figureSource,
        datasheetUrl: model.datasheetUrl,
        connectionType: inferConnectionType(mfg.name, model.name),
      });
    }
  }

  _cachedCameras = cameras;
  return cameras;
}

/**
 * IP-only camera slice used for SSG route generation + sitemap.
 *
 * We skip analog (HD-TVI / HDCVI / SD analog) SKUs from per-camera pages —
 * Google SERP demand for analog model strings is too low to justify the
 * build cost of N more prerendered pages. They remain reachable via the
 * runtime designer (getCameraBySlug works for the full catalog).
 *
 * We also skip catalog rows that are not cameras at all. The catalog is
 * harvested per manufacturer and carries their accessories too, and until
 * 2026-08-02 `connectionType === 'ip'` was the ONLY gate here, so 22 mounts,
 * encoders and multi-packs were getting camera pages — printing the catalog's
 * placeholder specs as if they were measured. The worst of them was live and
 * sitemapped: /camera/pelco-sarix-multi-enhanced-pendant-pole-or-wall-mount
 * described a wall mount as "8.3MP panoramic, 97°".
 *
 * Filtering here rather than in the sitemap script covers every consumer at
 * once: the SSG routes, the sitemap, and the NVR pages' camera pool.
 */
let _cachedStaticCameras: CameraPageData[] | null = null;

export function getAllCamerasForStaticPages(): CameraPageData[] {
  // Memoised for the same reason getAllCamerasWithSlugs() is. getRelatedCameras
  // and getCrossSellCameras now source from here, and the SSG calls both once
  // per camera page, so an uncached implementation would re-filter 22 892 rows
  // ~43 000 times per build.
  if (_cachedStaticCameras) return _cachedStaticCameras;
  _cachedStaticCameras = getAllCamerasWithSlugs()
    .filter(c => c.connectionType === 'ip')
    .filter(c => !isNonCamera(c.model))
    // Exact-duplicate slugs, retired 2026-08-26. Some models sit in the catalog
    // twice, once with the brand embedded in the model name and once without,
    // and generateCameraSlug() prefixes the brand either way — so the same
    // camera got /camera/axis-axis-p1487-le AND /camera/axis-p1487-le, with
    // byte-identical descriptions. Dropping the retired side here removes it
    // from the prerendered pages, the sitemap and the sibling rails at once;
    // public/.htaccess 301s the URL. getCameraBySlug() still resolves it, so
    // designer deep links and comparison pages are unaffected.
    .filter(c => !RETIRED_CAMERA_SLUG_SET.has(c.slug));
  return _cachedStaticCameras;
}

/** Get all slugs for SSG route generation (IP cameras only). */
export function getAllCameraSlugs(): string[] {
  // SKIP_CAMERA_PAGES=1 short-circuits per-camera SSG for hotfix builds.
  // Existing /camera/*.html files on the server stay (extract via
  // unzip-deploy.php does not delete absent files), so links don't 404.
  // Catalog data changes — full `npm run build` regenerates everything.
  if (typeof process !== 'undefined' && process.env?.SKIP_CAMERA_PAGES === '1') {
    return [];
  }
  return getAllCamerasForStaticPages().map(c => c.path.slice(1));
}

/** Legacy flat slug to canonical path, the input to the .htaccess 301 map. */
export function getCameraRedirectMap(): Array<{ from: string; to: string }> {
  return getAllCamerasForStaticPages()
    .filter(c => c.path !== `/camera/${c.slug}`)
    .map(c => ({ from: `/camera/${c.slug}`, to: c.path }));
}

/** Lazy-initialized slug→camera Map for O(1) lookups */
let slugMap: Map<string, CameraPageData> | null = null;

function getSlugMap(): Map<string, CameraPageData> {
  if (!slugMap) {
    slugMap = new Map();
    for (const cam of getAllCamerasWithSlugs()) {
      slugMap.set(cam.slug, cam);
    }
  }
  return slugMap;
}

/** Look up a single camera by slug */
export function getCameraBySlug(slug: string): CameraPageData | undefined {
  return getSlugMap().get(slug);
}

/** Lazy-initialized "brand/model" → camera Map for the root-level routes */
let _pathMap: Map<string, CameraPageData> | null = null;

/** Look up a single camera by its two URL segments, e.g. ("dahua", "ipc-hfw1230s-s5"). */
export function getCameraByPathSlugs(brandSlug: string, modelSlug: string): CameraPageData | undefined {
  if (!_pathMap) {
    _pathMap = new Map();
    for (const cam of getAllCamerasWithSlugs()) _pathMap.set(`${cam.brandSlug}/${cam.modelSlug}`, cam);
  }
  return _pathMap.get(`${brandSlug}/${modelSlug}`);
}

/**
 * Sibling pools for the related / cross-sell rails, built once.
 *
 * Both rails draw from getAllCamerasForStaticPages() rather than the whole
 * catalog: the catalog carries analog rows (hdcvi / turbo-hd / analog) and
 * non-camera accessories that never get a page, and sourcing links from the
 * unfiltered list emitted <a href> to URLs the server answers with 404.
 *
 * Grouping is precomputed because the SSG calls both rails once per camera
 * page. Doing the filter per call is ~22k rows scanned ~43k times per build.
 */
let _siblingPools: {
  sameType: Map<string, CameraPageData[]>;
  otherType: Map<string, CameraPageData[]>;
  posInType: Map<string, number>;
  posInMfg: Map<string, number>;
} | null = null;

function getSiblingPools() {
  if (_siblingPools) return _siblingPools;
  const sameType = new Map<string, CameraPageData[]>();
  const byMfg = new Map<string, CameraPageData[]>();
  for (const c of getAllCamerasForStaticPages()) {
    const k = `${c.manufacturer}||${c.type}`;
    let a = sameType.get(k); if (!a) { a = []; sameType.set(k, a); }
    a.push(c);
    let m = byMfg.get(c.manufacturer); if (!m) { m = []; byMfg.set(c.manufacturer, m); }
    m.push(c);
  }
  const posInType = new Map<string, number>();
  for (const [, arr] of sameType) arr.forEach((c, i) => posInType.set(c.slug, i));
  const posInMfg = new Map<string, number>();
  for (const [, arr] of byMfg) arr.forEach((c, i) => posInMfg.set(c.slug, i));
  // One cross-sell pool per (manufacturer, type): everything the manufacturer
  // makes EXCEPT this type. Holds references, not copies.
  const otherType = new Map<string, CameraPageData[]>();
  for (const k of sameType.keys()) {
    const [mfg, type] = k.split('||');
    otherType.set(k, (byMfg.get(mfg) ?? []).filter(c => c.type !== type));
  }
  _siblingPools = { sameType, otherType, posInType, posInMfg };
  return _siblingPools;
}

/**
 * Take `max` entries from `pool`, starting just past `start` and wrapping.
 *
 * The rotation is the whole point. Taking .slice(0, max) meant every member of
 * a (manufacturer, type) group linked the SAME first six siblings, so a bucket
 * of 3 000 Hikvision domes concentrated all of its internal links onto six
 * pages. Measured 2026-08-26: 19 281 of 21 782 camera pages (88.5%) had exactly
 * one inbound internal link — their brand hub — while the mean was 11.2 and one
 * page held 3 270. Anchoring each camera's window at its own index turns each
 * group into a ring: in any group of >= max + 1 every member sends `max` links
 * and receives exactly `max`, with no change to how many links a page emits.
 *
 * Groups smaller than `max` + 1 simply yield fewer links; nothing is padded in
 * from a wider set, because a "related" rail that reaches outside the
 * manufacturer is a product claim about relevance rather than a linking fix.
 * That case is rare: 417 cameras (1.9%) cannot fill the 6-slot rail and 35 have
 * no same-type sibling at all.
 */
function rotatedSlice(
  pool: CameraPageData[], selfSlug: string, start: number, max: number,
): CameraPageData[] {
  const out: CameraPageData[] = [];
  const n = pool.length;
  if (n === 0) return out;
  // `start` is -1 when the camera itself is not page-backed (an analog model
  // opened client-side). Falling back to 0 keeps the rail populated.
  const from = start >= 0 ? start : 0;
  for (let i = 1; i <= n && out.length < max; i++) {
    const c = pool[(from + i) % n];
    if (c.slug !== selfSlug) out.push(c);
  }
  return out;
}

/** Get related cameras (same manufacturer + type, max 6) */
export function getRelatedCameras(camera: CameraPageData, max = 6): CameraPageData[] {
  const { sameType, posInType } = getSiblingPools();
  const pool = sameType.get(`${camera.manufacturer}||${camera.type}`) ?? [];
  return rotatedSlice(pool, camera.slug, posInType.get(camera.slug) ?? -1, max);
}

/** Get cameras from same manufacturer but different type (max 4) */
export function getCrossSellCameras(camera: CameraPageData, max = 4): CameraPageData[] {
  const { otherType, posInMfg } = getSiblingPools();
  const pool = otherType.get(`${camera.manufacturer}||${camera.type}`) ?? [];
  // Anchored on position within the manufacturer, so cameras of the same type
  // spread across the cross-sell pool instead of stacking on its head.
  return rotatedSlice(pool, camera.slug, posInMfg.get(camera.slug) ?? -1, max);
}

/** Format resolution for display (e.g. "2560x1920" → "5MP (2560×1920)") */
export function formatResolution(resolution?: string): string {
  if (!resolution) return 'N/A';
  const parts = resolution.split(/[x×]/).map(Number);
  if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return resolution;
  const megapixels = (parts[0] * parts[1]) / 1_000_000;
  return `${megapixels.toFixed(1)}MP (${parts[0]}×${parts[1]})`;
}

/** Get megapixel value from resolution string */
export function getMegapixels(resolution?: string): number {
  if (!resolution) return 0;
  const parts = resolution.split(/[x×]/).map(Number);
  if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) {
    // Fallback: some catalog entries store resolution as the MP value directly
    // (e.g. "5MP", "12 MP") instead of WxH — extract it so titles don't read "0MP".
    const mp = resolution.match(/^\s*(\d+(?:\.\d+)?)\s*MP\b/i);
    return mp ? parseFloat(mp[1]) : 0;
  }
  return Math.round((parts[0] * parts[1]) / 1_000_000 * 10) / 10;
}

// EN 62676-4:2025 pixel-density thresholds, in pixels per metre.
const DORI_PX_PER_M = { detect: 25, observe: 62.5, recognize: 125, identify: 250 } as const;
export type DORILevel = keyof typeof DORI_PX_PER_M;

/**
 * Compute the maximum distance (m) at which a camera meets a given DORI
 * pixel-density threshold, derived from its resolution + horizontal FOV.
 *
 * The catalog's stored `range` field has been observed to be a placeholder
 * (it mirrors `irRange` on ~48% of entries — see camera-audit-2026-05-20.md).
 * This helper replaces that field for user-facing "effective range" display,
 * giving a deterministic number tied to the actual sensor + lens geometry
 * rather than an arbitrary import-time default.
 *
 * Defaults to "recognize" (125 px/m) — the threshold most installers mean by
 * "useful surveillance distance" (can describe what is happening, who is
 * doing it, even if not identify-grade).
 */
export function getDORIDistanceM(
  camera: Pick<CameraPageData, 'resolution' | 'hfov'>,
  level: DORILevel = 'recognize',
): number {
  const mp = getMegapixels(camera.resolution);
  if (!mp || !camera.hfov) return 0;
  // Horizontal pixel count from MP assuming 16:9 aspect:
  // mp = w * h ≈ w² * (9/16)  →  w ≈ √(mp * 16/9 * 1e6)
  const horizPx = Math.round(Math.sqrt(mp * (16 / 9) * 1_000_000));
  const tanHalf = Math.tan((camera.hfov * Math.PI) / 360);
  if (tanHalf <= 0) return 0;
  return Math.round(horizPx / (2 * DORI_PX_PER_M[level] * tanHalf));
}

/** Convenience: the user-facing "effective range" = DORI Recognize distance.
 *
 *  Falls back to the stored `range` field if DORI math can't compute
 *  (missing or unparseable resolution / hfov). About 937 entries hit the
 *  fallback as of 2026-05-20 — see camera-audit-2026-05-20.md. */
export function getEffectiveRangeM(camera: Pick<CameraPageData, 'resolution' | 'hfov' | 'range'>): number {
  const dori = getDORIDistanceM(camera, 'recognize');
  return dori > 0 ? dori : (camera.range || 0);
}

/** Camera type display name */
export function getCameraTypeName(type: string): string {
  const names: Record<string, string> = {
    bullet: 'Bullet Camera',
    dome: 'Dome Camera',
    ptz: 'PTZ Camera',
    panoramic: 'Panoramic Camera',
    turret: 'Turret Camera',
  };
  return names[type] || type;
}

/** Compact camera type name (no "Camera" suffix) for use in title tags where
 *  every char matters and the page already implies "camera" via context.
 *
 *  Optional `model` arg lets the function override the bucket name when the
 *  model itself is e.g. a "Turret" (Ubiquiti uses turret bodies that the
 *  catalog tags as type=dome). This avoids mislabeling Ubiquiti's AI Turret
 *  and G6 Turret lines as "dome" in SERP titles, which kills CTR on queries
 *  like "ubiquiti ai turret review" where users filter out generic dome
 *  results. Affects ~6 Ubiquiti SKUs. */
export function getCameraTypeShortName(type: string, model?: string): string {
  if (model && /\bturret\b/i.test(model)) return 'turret';
  const names: Record<string, string> = {
    bullet: 'bullet',
    dome: 'dome',
    ptz: 'PTZ',
    panoramic: 'panoramic',
    turret: 'turret',
  };
  return names[type] || type;
}
